<template>
  <div class="order-management">
    <div class="page-header">
      <!-- <h2>订单管理</h2> -->
      <div class="header-actions">
        <el-button type="primary" @click="handleAddOrder">
          <el-icon><Plus /></el-icon> 新建订单
        </el-button>
        <el-button type="success" @click="handleExport" :loading="exportLoading">
          <el-icon><Download /></el-icon> 导出订单
        </el-button>
      </div>
    </div>

    <!-- 订单统计卡片 -->
    <div class="stats-cards">
      <el-card class="stats-card total" shadow="hover">
        <div class="stats-card-title">总订单数</div>
        <div class="stats-card-value">{{ orderStats.total }}</div>
        <div class="stats-card-trend" v-if="orderStats.totalTrend">
          <span :class="orderStats.totalTrend > 0 ? 'up' : 'down'">
            {{ Math.abs(orderStats.totalTrend) }}%
          </span>
          较上月
        </div>
      </el-card>
      <el-card class="stats-card pending" shadow="hover">
        <div class="stats-card-title">待付款订单</div>
        <div class="stats-card-value">{{ orderStats.pending }}</div>
        <div class="stats-card-trend" v-if="orderStats.pendingTrend">
          <span :class="orderStats.pendingTrend > 0 ? 'up' : 'down'">
            {{ Math.abs(orderStats.pendingTrend) }}%
          </span>
          较上月
        </div>
      </el-card>
      <el-card class="stats-card processing" shadow="hover">
        <div class="stats-card-title">处理中订单</div>
        <div class="stats-card-value">{{ orderStats.processing }}</div>
        <div class="stats-card-trend" v-if="orderStats.processingTrend">
          <span :class="orderStats.processingTrend > 0 ? 'up' : 'down'">
            {{ Math.abs(orderStats.processingTrend) }}%
          </span>
          较上月
        </div>
      </el-card>
      <el-card class="stats-card completed" shadow="hover">
        <div class="stats-card-title">已完成订单</div>
        <div class="stats-card-value">{{ orderStats.completed }}</div>
        <div class="stats-card-trend" v-if="orderStats.completedTrend">
          <span :class="orderStats.completedTrend > 0 ? 'up' : 'down'">
            {{ Math.abs(orderStats.completedTrend) }}%
          </span>
          较上月
        </div>
      </el-card>
      <el-card class="stats-card cancelled" shadow="hover">
        <div class="stats-card-title">已取消订单</div>
        <div class="stats-card-value">{{ orderStats.cancelled }}</div>
        <div class="stats-card-trend" v-if="orderStats.cancelledTrend">
          <span :class="orderStats.cancelledTrend > 0 ? 'up' : 'down'">
            {{ Math.abs(orderStats.cancelledTrend) }}%
          </span>
          较上月
        </div>
      </el-card>
    </div>

    <el-card class="filter-card" v-loading="loading">
      <el-form :inline="true" :model="filterForm" class="filter-form">
        <el-form-item label="订单号">
          <el-input 
            v-model="filterForm.orderNo" 
            placeholder="请输入订单号" 
            clearable
            @keyup.enter="handleSearch">
          </el-input>
        </el-form-item>
        <el-form-item label="客户名称">
          <el-input 
            v-model="filterForm.customerName" 
            placeholder="请输入客户名称" 
            clearable
            @keyup.enter="handleSearch">
          </el-input>
        </el-form-item>
        <el-form-item label="订单状态">
          <el-select 
            v-model="filterForm.status" 
            placeholder="请选择订单状态" 
            clearable
            @change="handleSearch">
            <el-option label="待付款" value="pending"></el-option>
            <el-option label="处理中" value="processing"></el-option>
            <el-option label="已完成" value="completed"></el-option>
            <el-option label="已取消" value="cancelled"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="下单时间">
          <el-date-picker
            v-model="filterForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            :shortcuts="dateShortcuts"
            @change="handleSearch">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch" :loading="loading">
            <el-icon><Search /></el-icon> 查询
          </el-button>
          <el-button @click="resetForm" :disabled="loading">
            <el-icon><Refresh /></el-icon> 重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 批量操作工具栏 -->
    <div class="batch-toolbar" v-if="hasSelectedOrders">
      <span class="selected-count">已选择 {{ selectedOrders.length }} 项</span>
      <el-button type="danger" @click="handleBatchDelete" :disabled="!canBatchDelete">
        <el-icon><Delete /></el-icon> 批量删除
      </el-button>
    </div>

    <el-card class="table-card" v-loading="loading">
      <el-table 
        :data="orderList" 
        style="width: 100%"
        v-loading="loading"
        :empty-text="loading ? '加载中...' : '暂无数据'"
        @selection-change="handleSelectionChange"
        border
        stripe
        highlight-current-row>
        <el-table-column type="selection" width="55" fixed></el-table-column>
        <el-table-column prop="orderNo" label="订单号" width="180" fixed>
          <template #default="scope">
            <el-link type="primary" @click="handleView(scope.row)">{{ scope.row.orderNo }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="customerName" label="客户名称" min-width="120"></el-table-column>
        <el-table-column prop="totalAmount" label="订单金额" width="120">
          <template #default="scope">
            <span class="amount">¥{{ scope.row.totalAmount.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="订单状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)" effect="dark">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180"></el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button-group>
              <el-button 
                size="small" 
                @click="handleView(scope.row)">
                查看
              </el-button>
              <el-button 
                size="small" 
                type="primary" 
                @click="handleEdit(scope.row)"
                :disabled="scope.row.status === 'completed'">
                编辑
              </el-button>
              <el-button 
                size="small" 
                type="danger" 
                @click="handleDelete(scope.row)"
                :disabled="scope.row.status === 'completed'"
                :icon="Delete">
                删除
              </el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :disabled="loading">
        </el-pagination>
      </div>
    </el-card>

    <!-- 订单详情对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      v-model="dialogVisible" 
      width="800px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      destroy-on-close>
      <el-form 
        :model="orderForm" 
        :rules="rules" 
        ref="orderForm" 
        label-width="100px"
        v-loading="dialogLoading">
        <el-form-item label="客户" prop="customerId">
          <el-select 
            v-model="orderForm.customerId" 
            placeholder="请选择客户"
            :disabled="dialogTitle === '查看订单'"
            filterable
            remote
            :remote-method="handleCustomerSearch"
            :loading="customerSearchLoading">
            <el-option
              v-for="item in customerOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="商品" prop="products">
          <div v-for="(product, index) in orderForm.products" :key="index" class="product-item">
            <el-select 
              v-model="product.id" 
              placeholder="选择商品" 
              @change="handleProductChange($event, index)"
              :disabled="dialogTitle === '查看订单'"
              filterable
              remote
              :remote-method="(query) => handleProductSearch(query)"
              :loading="productSearchLoading">
              <el-option
                v-for="item in productOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
                <span>{{ item.label }}</span>
                <span class="product-price">¥{{ item.price }}</span>
              </el-option>
            </el-select>
            <el-input-number 
              v-model="product.quantity" 
              :min="1" 
              @change="calculateTotal"
              :disabled="dialogTitle === '查看订单'">
            </el-input-number>
            <el-input-number 
              v-model="product.price" 
              :min="0" 
              :precision="2" 
              @change="calculateTotal"
              :disabled="dialogTitle === '查看订单'">
            </el-input-number>
            <el-button 
              type="danger" 
              @click="removeProduct(index)"
              :disabled="dialogTitle === '查看订单'"
              :icon="Delete">
              删除
            </el-button>
          </div>
          <el-button 
            type="primary" 
            @click="addProduct"
            :disabled="dialogTitle === '查看订单'"
            :icon="Plus">
            添加商品
          </el-button>
        </el-form-item>
        <el-form-item label="订单金额">
          <span class="total-amount">¥{{ orderForm.totalAmount.toFixed(2) }}</span>
        </el-form-item>
        <el-form-item label="订单状态" prop="status">
          <el-select 
            v-model="orderForm.status" 
            placeholder="请选择订单状态"
            :disabled="dialogTitle === '查看订单'">
            <el-option label="待付款" value="pending"></el-option>
            <el-option label="处理中" value="processing"></el-option>
            <el-option label="已完成" value="completed"></el-option>
            <el-option label="已取消" value="cancelled"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input 
            type="textarea" 
            v-model="orderForm.remark"
            :disabled="dialogTitle === '查看订单'"
            :rows="3"
            placeholder="请输入订单备注信息">
          </el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取 消</el-button>
          <el-button 
            type="primary" 
            @click="submitForm"
            :loading="dialogLoading"
            v-if="dialogTitle !== '查看订单'">
            确 定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getOrderList, addOrder, updateOrder, deleteOrder, getOrderDetail } from '@/api/order'
import { ElMessage, ElMessageBox } from 'element-plus'
import { debounce } from 'lodash-es'
import * as XLSX from 'xlsx'
import { 
  Plus, 
  Delete, 
  Search, 
  Refresh, 
  Download 
} from '@element-plus/icons-vue'

export default {
  name: 'OrderManagement',
  components: {
    Plus,
    Delete,
    Search,
    Refresh,
    Download
  },
  data() {
    return {
      loading: false,
      dialogLoading: false,
      exportLoading: false,
      customerSearchLoading: false,
      productSearchLoading: false,
      filterForm: {
        orderNo: '',
        customerName: '',
        status: '',
        dateRange: []
      },
      dateShortcuts: [
        {
          text: '最近一周',
          value: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
            return [start, end]
          }
        },
        {
          text: '最近一个月',
          value: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            return [start, end]
          }
        },
        {
          text: '最近三个月',
          value: () => {
            const end = new Date()
            const start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
            return [start, end]
          }
        }
      ],
      orderList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      dialogVisible: false,
      dialogTitle: '添加订单',
      orderForm: {
        customerId: '',
        products: [],
        totalAmount: 0,
        status: 'pending',
        remark: ''
      },
      rules: {
        customerId: [
          { required: true, message: '请选择客户', trigger: 'change' }
        ],
        products: [
          { required: true, message: '请选择商品', trigger: 'change' },
          { 
            validator: (rule, value, callback) => {
              if (value.length === 0) {
                callback(new Error('请至少添加一个商品'))
              } else if (value.some(item => !item.id)) {
                callback(new Error('请选择商品'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ],
        status: [
          { required: true, message: '请选择订单状态', trigger: 'change' }
        ]
      },
      productOptions: [],
      customerOptions: [],
      selectedOrders: [],
      orderStats: {
        total: 0,
        pending: 0,
        processing: 0,
        completed: 0,
        cancelled: 0,
        totalTrend: 0,
        pendingTrend: 0,
        processingTrend: 0,
        completedTrend: 0,
        cancelledTrend: 0
      }
    }
  },
  computed: {
    hasSelectedOrders() {
      return this.selectedOrders.length > 0
    },
    canBatchDelete() {
      return this.selectedOrders.every(order => order.status !== 'completed')
    }
  },
  created() {
    this.fetchData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleSearch: debounce(function() {
      this.currentPage = 1
      this.fetchData()
    }, 300),

    async fetchData() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          ...this.filterForm,
          startDate: this.filterForm.dateRange?.[0],
          endDate: this.filterForm.dateRange?.[1]
        }
        const res = await getOrderList(params)
        this.orderList = res.data.list
        this.total = res.data.total
        this.orderStats = res.data.stats || this.orderStats
      } catch (error) {
        ElMessage.error('获取订单列表失败')
        console.error('获取订单列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    handleSelectionChange(selection) {
      this.selectedOrders = selection
    },

    async handleBatchDelete() {
      if (!this.canBatchDelete) {
        ElMessage.warning('已完成的订单不能删除')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确认删除选中的 ${this.selectedOrders.length} 个订单吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const orderIds = this.selectedOrders.map(order => order.id)
        await Promise.all(orderIds.map(id => deleteOrder(id)))
        
        ElMessage.success('批量删除成功')
        this.fetchData()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('批量删除失败')
          console.error('批量删除失败:', error)
        }
      }
    },

    async handleExport() {
      this.exportLoading = true
      try {
        const params = {
          ...this.filterForm,
          startDate: this.filterForm.dateRange?.[0],
          endDate: this.filterForm.dateRange?.[1]
        }
        const res = await getOrderList({ ...params, pageSize: 9999 })
        
        const exportData = res.data.list.map(order => ({
          '订单号': order.orderNo,
          '客户名称': order.customerName,
          '订单金额': `¥${order.totalAmount.toFixed(2)}`,
          '订单状态': this.getStatusText(order.status),
          '创建时间': order.createTime,
          '备注': order.remark || ''
        }))

        const worksheet = XLSX.utils.json_to_sheet(exportData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, '订单列表')
        
        const wscols = [
          { wch: 15 }, // 订单号
          { wch: 10 }, // 客户名称
          { wch: 12 }, // 订单金额
          { wch: 10 }, // 订单状态
          { wch: 20 }, // 创建时间
          { wch: 30 }  // 备注
        ]
        worksheet['!cols'] = wscols

        const fileName = `订单列表_${new Date().toLocaleDateString()}.xlsx`
        XLSX.writeFile(workbook, fileName)
        
        ElMessage.success('导出成功')
      } catch (error) {
        ElMessage.error('导出失败')
        console.error('导出失败:', error)
      } finally {
        this.exportLoading = false
      }
    },

    handleResize() {
      // 处理表格列宽自适应
      const table = this.$refs.orderTable
      if (table) {
        table.doLayout()
      }
    },

    getStatusType(status) {
      const statusMap = {
        pending: 'warning',
        processing: 'primary',
        completed: 'success',
        cancelled: 'info'
      }
      return statusMap[status] || 'info'
    },

    getStatusText(status) {
      const statusMap = {
        pending: '待付款',
        processing: '处理中',
        completed: '已完成',
        cancelled: '已取消'
      }
      return statusMap[status] || status
    },

    resetForm() {
      this.filterForm = {
        orderNo: '',
        customerName: '',
        status: '',
        dateRange: []
      }
      this.handleSearch()
    },

    handleSizeChange(val) {
      this.pageSize = val
      this.fetchData()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchData()
    },

    handleAddOrder() {
      this.dialogTitle = '添加订单'
      this.orderForm = {
        customerId: '',
        products: [],
        totalAmount: 0,
        status: 'pending',
        remark: ''
      }
      this.dialogVisible = true
    },

    handleView(row) {
      this.dialogTitle = '查看订单'
      this.orderForm = { ...row }
      this.dialogVisible = true
    },

    async handleEdit(row) {
      this.dialogLoading = true
      try {
        const res = await getOrderDetail(row.id)
        this.dialogTitle = '编辑订单'
        this.orderForm = res.data
        this.dialogVisible = true
      } catch (error) {
        ElMessage.error('获取订单详情失败')
        console.error('获取订单详情失败:', error)
      } finally {
        this.dialogLoading = false
      }
    },

    async handleDelete(row) {
      try {
        await ElMessageBox.confirm('确认删除该订单吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteOrder(row.id)
        ElMessage.success('删除成功')
        this.fetchData()
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除订单失败')
          console.error('删除订单失败:', error)
        }
      }
    },

    addProduct() {
      this.orderForm.products.push({
        id: '',
        quantity: 1,
        price: 0
      })
    },

    removeProduct(index) {
      this.orderForm.products.splice(index, 1)
      this.calculateTotal()
    },

    handleProductChange(productId, index) {
      const product = this.productOptions.find(p => p.value === productId)
      if (product) {
        this.orderForm.products[index].price = product.price
        this.calculateTotal()
      }
    },

    calculateTotal() {
      this.orderForm.totalAmount = this.orderForm.products.reduce((total, product) => {
        return total + (product.quantity * product.price)
      }, 0)
    },

    async handleCustomerSearch(query) {
      if (query) {
        this.customerSearchLoading = true
        try {
          // 这里应该调用后端API进行客户搜索
          // const res = await searchCustomers(query)
          // this.customerOptions = res.data
          this.customerOptions = [
            { value: 'C001', label: '张三' },
            { value: 'C002', label: '李四' }
          ]
        } catch (error) {
          console.error('搜索客户失败:', error)
        } finally {
          this.customerSearchLoading = false
        }
      } else {
        this.customerOptions = []
      }
    },

    async handleProductSearch(query) {
      if (query) {
        this.productSearchLoading = true
        try {
          // 这里应该调用后端API进行商品搜索
          // const res = await searchProducts(query)
          // this.productOptions = res.data
          this.productOptions = [
            { value: 'P001', label: '商品A', price: 100 },
            { value: 'P002', label: '商品B', price: 200 },
            { value: 'P003', label: '商品C', price: 300 }
          ]
        } catch (error) {
          console.error('搜索商品失败:', error)
        } finally {
          this.productSearchLoading = false
        }
      } else {
        this.productOptions = []
      }
    },

    async submitForm() {
      try {
        await this.$refs.orderForm.validate()
        this.dialogLoading = true
        if (this.orderForm.id) {
          await updateOrder(this.orderForm.id, this.orderForm)
          ElMessage.success('更新成功')
        } else {
          await addOrder(this.orderForm)
          ElMessage.success('添加成功')
        }
        this.dialogVisible = false
        this.fetchData()
      } catch (error) {
        if (error.message) {
          ElMessage.error(error.message)
        }
        console.error('提交表单失败:', error)
      } finally {
        this.dialogLoading = false
      }
    }
  }
}
</script>

<style scoped>
.order-management {
  /* padding: 20px; */
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.stats-card {
  padding: 20px;
  border-radius: 4px;
  background-color: #fff;
  transition: all 0.3s;
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stats-card-title {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.stats-card-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 10px;
}

.stats-card-trend {
  font-size: 12px;
  color: #909399;
}

.stats-card-trend .up {
  color: #67c23a;
}

.stats-card-trend .down {
  color: #f56c6c;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.batch-toolbar {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.selected-count {
  color: #909399;
  font-size: 14px;
}

.table-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.product-item {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: center;
}

.product-item .el-select {
  width: 200px;
}

.product-item .el-input-number {
  width: 120px;
}

.product-price {
  float: right;
  color: #909399;
  font-size: 12px;
}

.total-amount {
  font-size: 18px;
  font-weight: bold;
  color: #f56c6c;
}

.amount {
  color: #f56c6c;
  font-weight: bold;
}

/* 响应式布局 */
@media screen and (max-width: 768px) {
  .filter-form {
    flex-direction: column;
  }
  
  .filter-form .el-form-item {
    margin-right: 0;
    width: 100%;
  }
  
  .product-item {
    flex-direction: column;
    align-items: stretch;
  }
  
  .product-item .el-select,
  .product-item .el-input-number {
    width: 100%;
  }

  .header-actions {
    flex-direction: column;
  }

  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style> 